import React from 'react';
import { bool, func, object, shape, string, oneOf } from 'prop-types';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { intlShape, injectIntl } from '../../util/reactIntl';
import { connect } from 'react-redux';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  LISTING_PAGE_PARAM_TYPES,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  createSlug,
} from '../../util/urlHelpers';
import { LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL, propTypes } from '../../util/types';
import { ensureOwnListing } from '../../util/data';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import {
  stripeAccountClearError,
  createStripeAccount,
  getStripeConnectAccountLink,
} from '../../ducks/stripeConnectAccount.duck';

import { NamedRedirect, Page } from '../../components';
// import { TopbarContainer } from '../../containers';

import {
  requestFetchBookings,
  requestFetchAvailabilityExceptions,
  requestCreateAvailabilityException,
  requestDeleteAvailabilityException,
  requestCreateListingDraft,
  requestPublishListingDraft,
  requestUpdateListing,
  requestImageUpload,
  updateImageOrder,
  removeListingImage,
  loadData,
  clearUpdatedTab,
  savePayoutDetails,
} from './EditListingPage.duck';

// import css from './EditListingPage.css';

import NavBar from '../../custom-components/elements/nav-bar/NavBar';
import Footer from '../../custom-components/elements/footer/Footer';

import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import NavBarWrapper from '../../custom-components/wrappers/navbar-wrapper/NavBarWrapper';
import ContainerWrapper from '../../custom-components/wrappers/container-wrapper/ContainerWrapper';
// import DashboardLayoutWrapper from '../../custom-components/wrappers/dashboard-layout-wrapper/DashboardLayoutWrapper';
import DashboardAddListingWrapper from '../../custom-components/wrappers/dashboard-add-listing-wrapper/DashboardAddListingWrapper';
import MainDashboardWrapper from '../../components/MainDashboardWrapper/MainDashboardWrapper';

const STRIPE_ONBOARDING_RETURN_URL_SUCCESS = 'success';
const STRIPE_ONBOARDING_RETURN_URL_FAILURE = 'failure';
const STRIPE_ONBOARDING_RETURN_URL_TYPES = [
  STRIPE_ONBOARDING_RETURN_URL_SUCCESS,
  STRIPE_ONBOARDING_RETURN_URL_FAILURE,
];

const { UUID } = sdkTypes;

// Read the coach's subscription tier (0 = none, 1 = Network, 2 = Verified).
// Sharetribe stores it in profile.metadata.subscriptionDetails — set by the
// subscription-service webhook after each Chargebee subscription event.
const getSubscriptionTier = currentUser => {
  if (!currentUser) return 0;
  const meta =
    currentUser.attributes &&
    currentUser.attributes.profile &&
    currentUser.attributes.profile.metadata;
  if (!meta || !meta.subscriptionDetails) return 0;
  const tier = meta.subscriptionDetails.subscription;
  return typeof tier === 'number' ? tier : 0;
};

// N.B. All the presentational content needs to be extracted to their own components
export const EditListingPageComponent = props => {
  const {
    currentUser,
    // createStripeAccountError,
    // fetchInProgress,
    getOwnListing,
    history,
    // intl,
    // onFetchAvailabilityExceptions,
    // onCreateAvailabilityException,
    // onDeleteAvailabilityException,
    // onFetchBookings,
    onCreateListingDraft,
    onPublishListingDraft,
    onUpdateListing,
    onImageUpload,
    // onRemoveListingImage,
    // onManageDisableScrolling,
    // onPayoutDetailsFormSubmit,
    // onPayoutDetailsFormChange,
    // onGetStripeConnectAccountLink,
    // onUpdateImageOrder,
    // onChange,
    page,
    params,
    scrollingDisabled,
    // stripeAccountFetched,
    // stripeAccount,
  } = props;

  const { id, type, returnURLType } = params;
  const isNewURI = type === LISTING_PAGE_PARAM_TYPE_NEW;
  const isDraftURI = type === LISTING_PAGE_PARAM_TYPE_DRAFT;
  const isNewListingFlow = isNewURI || isDraftURI;

  // ── Subscription gate for publish ─────────────────────────────────────
  // A coach without an active subscription (tier 0) can build and save
  // listings as drafts freely. But the moment they try to publish, we
  // intercept and redirect to the pricing tab. This is the runtime
  // enforcement of Section 8.6 item 2 in the gating spec.
  //
  // Draft creation, draft updates, and image uploads all stay open. Only
  // the publish transition is gated.
  const subscriptionTier = getSubscriptionTier(currentUser);
  const canPublish = subscriptionTier >= 1;
  const gatedPublishListingDraft = listingId => {
    if (canPublish) {
      return onPublishListingDraft(listingId);
    }
    // Park the listingId so the pricing page can pick the publish back up
    // after subscription. (Read by the welcome page / pricing page —
    // optional polish, not required for the gate to function.)
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem(
          'pendingPublishListingId',
          listingId && listingId.uuid ? listingId.uuid : String(listingId)
        );
      }
    } catch (e) {
      // sessionStorage may be disabled — non-fatal.
    }
    // Funnel analytics: emit publish_gate_blocked so we can measure how
    // often the gate fires (and against which user segments).
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'publish_gate_blocked',
        listing_id: listingId && listingId.uuid ? listingId.uuid : String(listingId),
        current_tier: subscriptionTier,
      });
    }
    if (history && history.push) {
      history.push('/account/payments/pricing?gate=publish');
    }
    return undefined;
  };

  const listingId = page.submittedListingId || (id ? new UUID(id) : null);
  const currentListing = ensureOwnListing(getOwnListing(listingId));
  const { state: currentListingState } = currentListing.attributes;

  const isPastDraft = currentListingState && currentListingState !== LISTING_STATE_DRAFT;
  const shouldRedirect = isNewListingFlow && listingId && isPastDraft;

  const hasStripeOnboardingDataIfNeeded = returnURLType ? !!(currentUser && currentUser.id) : true;
  const showForm = hasStripeOnboardingDataIfNeeded && (isNewURI || currentListing.id);

  if (shouldRedirect) {
    const isPendingApproval =
      currentListing && currentListingState === LISTING_STATE_PENDING_APPROVAL;

    // If page has already listingId (after submit) and current listings exist
    // redirect to listing page
    const listingSlug = currentListing ? createSlug(currentListing.attributes.title) : null;

    const redirectProps = isPendingApproval
      ? {
          name: 'ListingPageVariant',
          params: {
            id: listingId.uuid,
            slug: listingSlug,
            variant: LISTING_PAGE_PENDING_APPROVAL_VARIANT,
          },
        }
      : {
          name: 'ListingPage',
          params: {
            id: listingId.uuid,
            slug: listingSlug,
          },
        };

    return <NamedRedirect {...redirectProps} />;
  } else if (showForm) {
    // const {
    //   createListingDraftError = null,
    //   publishListingError = null,
    //   updateListingError = null,
    //   showListingsError = null,
    //   uploadImageError = null,
    // } = page;
    // const errors = {
    //   createListingDraftError,
    //   publishListingError,
    //   updateListingError,
    //   showListingsError,
    //   uploadImageError,
    //   createStripeAccountError,
    // };
    // TODO: is this dead code? (shouldRedirect is checked before)
    // const newListingPublished =
    //   isDraftURI && currentListing && currentListingState !== LISTING_STATE_DRAFT;

    // Show form if user is posting a new listing or editing existing one
    // const disableForm = page.redirectToListing && !showListingsError;

    // Images are passed to EditListingForm so that it can generate thumbnails out of them
    const currentListingImages =
      currentListing && currentListing.images ? currentListing.images : [];

    // Images not yet connected to the listing
    const imageOrder = page.imageOrder || [];
    const unattachedImages = imageOrder.map(i => page.images[i]);

    const allImages = currentListingImages.concat(unattachedImages);
    const removedImageIds = page.removedImageIds || [];
    const images = allImages.filter(img => {
      return !removedImageIds.includes(img.id);
    });

    // const title = isNewListingFlow
    //   ? intl.formatMessage({ id: 'EditListingPage.titleCreateListing' })
    //   : intl.formatMessage({ id: 'EditListingPage.titleEditListing' });

    return (
      <PageWrapper>
        <MainDashboardWrapper title={'Ads'} page={'coach-ads'}>
          {/* <DashboardLayoutWrapper page="listings"> */}
          <DashboardAddListingWrapper
            listing={currentListing}
            onUpdateListing={onUpdateListing}
            onCreateListingDraft={onCreateListingDraft}
            onPublishListingDraft={gatedPublishListingDraft}
            params={params}
            onImageUpload={onImageUpload}
            images={images}
            currentUser={currentUser}
          />
          {/* </DashboardLayoutWrapper> */}
        </MainDashboardWrapper>
      </PageWrapper>
    );
  } else {
    // If user has come to this page through a direct linkto edit existing listing,
    // we need to load it first.
    // const loadingPageMsg = {
    //   id: 'EditListingPage.loadingListingData',
    // };
    return <Page scrollingDisabled={scrollingDisabled} />;
  }
};

EditListingPageComponent.defaultProps = {
  createStripeAccountError: null,
  currentUser: null,
  currentUserHasOrders: null,
  listing: null,
  listingDraft: null,
  notificationCount: 0,
  sendVerificationEmailError: null,
};

EditListingPageComponent.propTypes = {
  createStripeAccountError: propTypes.error,
  currentUser: propTypes.currentUser,
  fetchInProgress: bool.isRequired,
  getOwnListing: func.isRequired,
  onFetchAvailabilityExceptions: func.isRequired,
  onCreateAvailabilityException: func.isRequired,
  onCreateListingDraft: func.isRequired,
  onPublishListingDraft: func.isRequired,
  onImageUpload: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onPayoutDetailsFormChange: func.isRequired,
  onPayoutDetailsSubmit: func.isRequired,
  onUpdateImageOrder: func.isRequired,
  onRemoveListingImage: func.isRequired,
  onUpdateListing: func.isRequired,
  onChange: func.isRequired,
  page: object.isRequired,
  params: shape({
    id: string.isRequired,
    slug: string.isRequired,
    type: oneOf(LISTING_PAGE_PARAM_TYPES).isRequired,
    tab: string.isRequired,
    returnURLType: oneOf(STRIPE_ONBOARDING_RETURN_URL_TYPES),
  }).isRequired,
  scrollingDisabled: bool.isRequired,

  /* from withRouter */
  history: shape({
    push: func.isRequired,
  }).isRequired,

  /* from injectIntl */
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const page = state.EditListingPage;
  const {
    getAccountLinkInProgress,
    createStripeAccountInProgress,
    createStripeAccountError,
    updateStripeAccountError,
    fetchStripeAccountError,
    stripeAccount,
    stripeAccountFetched,
  } = state.stripeConnectAccount;

  const { currentUser } = state.user;

  const fetchInProgress = createStripeAccountInProgress;

  const getOwnListing = id => {
    const listings = getMarketplaceEntities(state, [{ id, type: 'ownListing' }]);

    return listings.length === 1 ? listings[0] : null;
  };
  return {
    getAccountLinkInProgress,
    createStripeAccountError,
    updateStripeAccountError,
    fetchStripeAccountError,
    stripeAccount,
    stripeAccountFetched,
    currentUser,
    fetchInProgress,
    getOwnListing,
    page,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateListing: (tab, values) => dispatch(requestUpdateListing(tab, values)),
  onFetchBookings: params => dispatch(requestFetchBookings(params)),
  onFetchAvailabilityExceptions: params => dispatch(requestFetchAvailabilityExceptions(params)),
  onCreateAvailabilityException: params => dispatch(requestCreateAvailabilityException(params)),
  onDeleteAvailabilityException: params => dispatch(requestDeleteAvailabilityException(params)),
  onCreateListingDraft: values => dispatch(requestCreateListingDraft(values)),
  onPublishListingDraft: listingId => dispatch(requestPublishListingDraft(listingId)),
  onImageUpload: data => dispatch(requestImageUpload(data)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onPayoutDetailsFormChange: () => dispatch(stripeAccountClearError()),
  onPayoutDetailsSubmit: values => dispatch(createStripeAccount(values)),
  onPayoutDetailsFormSubmit: (values, isUpdateCall) =>
    dispatch(savePayoutDetails(values, isUpdateCall)),
  onGetStripeConnectAccountLink: params => dispatch(getStripeConnectAccountLink(params)),
  onUpdateImageOrder: imageOrder => dispatch(updateImageOrder(imageOrder)),
  onRemoveListingImage: imageId => dispatch(removeListingImage(imageId)),
  onChange: () => dispatch(clearUpdatedTab()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const EditListingPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(injectIntl(EditListingPageComponent));

EditListingPage.loadData = loadData;

export default EditListingPage;
